import React from 'react';
import { Package, Warehouse, Shield, TrendingUp } from 'lucide-react';

const About = () => {
  const importancePoints = [
    {
      icon: Package,
      title: 'Shipping Container Protection',
      description: 'During long ocean freight voyages, containers face extreme temperature swings that cause condensation - a phenomenon known as "container rain". NOMOISTURE calcium chloride desiccant bags deliver export cargo humidity protection, preventing container rain, mold, rust and corrosion on high-value shipments.',
      image: 'https://images.pexels.com/photos/5615436/pexels-photo-5615436.jpeg',
      alt: 'NOMOISTURE calcium chloride container desiccant bag hanging inside a shipping container to prevent container rain during ocean freight export',
    },
    {
      icon: Warehouse,
      title: 'Warehouse Humidity Control',
      description: 'Warehouses store products for extended periods where high humidity silently causes wastage and damage. NOMOISTURE desiccants and silica gel pouches maintain optimal humidity control, preserving product quality, extending shelf life and reducing moisture damage across storage and export packaging.',
      image: 'https://images.pexels.com/photos/4487382/pexels-photo-4487382.jpeg',
      alt: 'Industrial warehouse using NOMOISTURE silica gel pouches and container desiccants for humidity control and moisture damage control',
    },
    {
      icon: Shield,
      title: 'Export Cargo Moisture Protection',
      description: 'Moisture is the silent destroyer of stored goods - from electronics and pharmaceuticals to textiles, agro and food products. NOMOISTURE desiccants create a protective barrier of moisture damage control, keeping export packaging dry from factory floor to final destination port.',
      image: 'https://images.pexels.com/photos/1624695/pexels-photo-1624695.jpeg',
      alt: 'Export cargo boxes protected by NOMOISTURE desiccants for moisture damage control and export packaging integrity',
    },
  ];

  return (
    <section 
      id="about"
      style={{
        background: '#0a0e1a',
        padding: 'clamp(3rem, 8vw, 5rem) 1.5rem',
      }}
    >
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
      }}>
        {/* About Section */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(236, 236, 117, 0.15)',
            border: '1px solid rgba(236, 236, 117, 0.3)',
            padding: '0.5rem 1.5rem',
            borderRadius: '50px',
            marginBottom: '1.5rem',
          }}>
            <TrendingUp size={20} style={{ color: '#ECEC75' }} />
            <span style={{ 
              fontSize: '0.875rem', 
              fontWeight: 600,
              color: '#ECEC75',
            }}>
              About NOMOISTURE
            </span>
          </div>

          <h2 style={{
            fontFamily: "'Crimson Text', serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#f1f5f9',
            marginBottom: '1.5rem',
          }}>
            Your Trusted Partner for Container Desiccants &amp; Export Cargo Moisture Protection
          </h2>

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: '#cbd5e1',
            lineHeight: 1.8,
            maxWidth: '820px',
            margin: '0 auto 3rem',
          }}>
            NOMOISTURE is an Indian manufacturer and supplier of premium industrial desiccants -
            high-absorption calcium chloride desiccant bags and silica gel pouches - engineered to prevent
            container rain in ocean freight and deliver dependable export cargo humidity protection.
            Humidity silently damages products, drives wastage, hurts quality and inflates losses across
            storage, transport and exports. That is where NOMOISTURE steps in. Our high-performance
            NOMOISTURE desiccants actively control moisture, safeguarding raw materials and finished goods
            from mold, spoilage, corrosion and degradation. From warehouses to export containers - and
            across food and agro products, pharmaceuticals, textiles and FMCG - our solutions are designed
            for real-world moisture damage control, extending shelf life and preserving quality even in
            the most humid conditions.
          </p>
          
        </div>

        {/* Importance Section */}
        <div style={{
          marginTop: '5rem',
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '4rem',
          }}>
            <h2 style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 700,
              color: '#f1f5f9',
              marginBottom: '1rem',
            }}>
              Why NOMOISTURE Desiccants Are Critical for Exporters
            </h2>
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              color: '#cbd5e1',
              maxWidth: '720px',
              margin: '0 auto',
            }}>
              From preventing container rain in ocean freight to warehouse humidity control -
              here is why NOMOISTURE desiccants belong in every export packaging plan.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '3rem',
          }}>
            {importancePoints.map((point, index) => (
              <div 
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '2rem',
                  alignItems: 'center',
                  background: '#121827',
                  border: '1px solid rgba(236, 236, 117, 0.1)',
                  borderRadius: '12px',
                  padding: '2rem',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.4)',
                }}
                className="importance-card"
              >
                <div style={{
                  order: index % 2 === 0 ? 1 : 2,
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                  }}>
                    <div style={{
                      background: 'rgba(236, 236, 117, 0.15)',
                      border: '1px solid rgba(236, 236, 117, 0.3)',
                      color: '#ECEC75',
                      padding: '1rem',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <point.icon size={32} />
                    </div>
                    <h3 style={{
                      fontFamily: "'Crimson Text', serif",
                      fontSize: 'clamp(1.5rem, 3vw, 1.75rem)',
                      fontWeight: 700,
                      color: '#f1f5f9',
                      margin: 0,
                    }}>
                      {point.title}
                    </h3>
                  </div>
                  <p style={{
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    color: '#cbd5e1',
                    lineHeight: 1.8,
                    margin: 0,
                  }}>
                    {point.description}
                  </p>
                </div>
                <div style={{
                  order: index % 2 === 0 ? 2 : 1,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  height: '300px',
                }}>
                  <img 
                    src={point.image}
                    alt={point.alt}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .importance-card {
            grid-template-columns: 1fr 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default About;
